// 简单的 Markdown 渲染器

class MarkdownRenderer {
  constructor() {
    this.rules = [
      // 代码块（必须在其他规则之前）
      {
        pattern: /```(\w+)?\n([\s\S]*?)```/g,
        replacement: '<pre><code class="language-$1">$2</code></pre>'
      },
      
      // 标题
      {
        pattern: /^### (.*$)/gim,
        replacement: '<h3>$1</h3>'
      },
      {
        pattern: /^## (.*$)/gim,
        replacement: '<h2>$1</h2>'
      },
      {
        pattern: /^# (.*$)/gim,
        replacement: '<h1>$1</h1>'
      },
      
      // 粗体和斜体
      {
        pattern: /\*\*\*(.*?)\*\*\*/g,
        replacement: '<strong><em>$1</em></strong>'
      },
      {
        pattern: /\*\*(.*?)\*\*/g,
        replacement: '<strong>$1</strong>'
      },
      {
        pattern: /\*(.*?)\*/g,
        replacement: '<em>$1</em>'
      },
      
      // 行内代码
      {
        pattern: /`([^`]+)`/g,
        replacement: '<code>$1</code>'
      },
      
      // 链接
      {
        pattern: /\[([^\]]+)\]\(([^)]+)\)/g,
        replacement: '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      },
      
      // 图片
      {
        pattern: /!\[([^\]]*)\]\(([^)]+)\)/g,
        replacement: '<img src="$2" alt="$1" />'
      },
      
      // 水平线
      {
        pattern: /^---$/gm,
        replacement: '<hr>'
      },
      
      // 引用
      {
        pattern: /^> (.*$)/gim,
        replacement: '<blockquote>$1</blockquote>'
      }
    ]
  }
  
  render(markdown) {
    if (!markdown) return ''
    
    let html = markdown
    
    // 应用所有规则
    this.rules.forEach(rule => {
      html = html.replace(rule.pattern, rule.replacement)
    })
    
    // 处理列表
    html = this.renderLists(html)
    
    // 处理段落
    html = this.renderParagraphs(html)
    
    // 清理和优化
    html = this.cleanup(html)
    
    return html
  }
  
  renderLists(html) {
    // 无序列表
    html = html.replace(/^[\s]*\* (.*$)/gim, '<li>$1</li>')
    html = html.replace(/^[\s]*- (.*$)/gim, '<li>$1</li>')
    
    // 有序列表
    html = html.replace(/^[\s]*\d+\. (.*$)/gim, '<li>$1</li>')
    
    // 包装连续的 li 标签
    html = html.replace(/(<li>.*<\/li>)/gims, (match) => {
      const items = match.match(/<li>.*?<\/li>/g)
      if (items && items.length > 0) {
        return `<ul>${items.join('')}</ul>`
      }
      return match
    })
    
    return html
  }
  
  renderParagraphs(html) {
    // 分割成行
    const lines = html.split('\n')
    const result = []
    let inCodeBlock = false
    let inList = false
    let currentParagraph = []
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      
      // 检查是否在代码块中
      if (line.includes('<pre>')) inCodeBlock = true
      if (line.includes('</pre>')) inCodeBlock = false
      
      // 检查是否在列表中
      if (line.includes('<ul>') || line.includes('<ol>')) inList = true
      if (line.includes('</ul>') || line.includes('</ol>')) inList = false
      
      // 跳过空行和已经是HTML标签的行
      if (!line || 
          inCodeBlock || 
          inList ||
          line.startsWith('<h') || 
          line.startsWith('<pre') || 
          line.startsWith('<ul') || 
          line.startsWith('<ol') ||
          line.startsWith('<blockquote') ||
          line.startsWith('<hr') ||
          line === '</pre>' ||
          line === '</ul>' ||
          line === '</ol>' ||
          line === '</blockquote>') {
        
        // 如果有累积的段落内容，先处理它
        if (currentParagraph.length > 0) {
          result.push(`<p>${currentParagraph.join(' ')}</p>`)
          currentParagraph = []
        }
        
        result.push(line)
      } else {
        // 累积段落内容
        currentParagraph.push(line)
      }
    }
    
    // 处理最后的段落
    if (currentParagraph.length > 0) {
      result.push(`<p>${currentParagraph.join(' ')}</p>`)
    }
    
    return result.join('\n')
  }
  
  cleanup(html) {
    return html
      // 移除多余的空行
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      // 清理段落标签
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/<p>(<h[1-6]>)/g, '$1')
      .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
      .replace(/<p>(<ul>|<ol>|<pre>|<blockquote>|<hr>)/g, '$1')
      .replace(/(<\/ul>|<\/ol>|<\/pre>|<\/blockquote>|<hr>)<\/p>/g, '$1')
      // 清理列表
      .replace(/<ul>\s*<ul>/g, '<ul>')
      .replace(/<\/ul>\s*<\/ul>/g, '</ul>')
      // 清理引用
      .replace(/<blockquote>\s*<blockquote>/g, '<blockquote>')
      .replace(/<\/blockquote>\s*<\/blockquote>/g, '</blockquote>')
      .trim()
  }
  
  // 提取摘要（前N个字符，不包含HTML标签）
  extractExcerpt(markdown, maxLength = 200) {
    const html = this.render(markdown)
    const text = html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    
    if (text.length <= maxLength) {
      return text
    }
    
    return text.substring(0, maxLength).replace(/\s+\S*$/, '') + '...'
  }
  
  // 估算阅读时间（基于字数）
  estimateReadTime(markdown, wordsPerMinute = 200) {
    const text = markdown.replace(/[#*`\[\]()]/g, '').replace(/\s+/g, ' ')
    const wordCount = text.split(' ').length
    const minutes = Math.ceil(wordCount / wordsPerMinute)
    return Math.max(1, minutes)
  }
}

// 创建单例实例
const markdownRenderer = new MarkdownRenderer()

export default markdownRenderer

// 导出便捷方法
export const { render, extractExcerpt, estimateReadTime } = markdownRenderer